import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Analytics, setUserId } from '@angular/fire/analytics';
import { Auth, user } from '@angular/fire/auth';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CookieConsentComponent } from '@components/cookie-consent/cookie-consent.component';
import { CookieKeyEnum } from '@shared/enums/cookie-key.enum';
import { ModalPositionEnum } from '@shared/enums/modal-position.enum';
import { SleipModalOverlayConfig } from '@shared/models/modal-config.model';
import { BrowserStorageService } from '@shared/services/browser-storage.service';
import { GoogleAnalyticsService } from '@shared/services/google-analytics.service';
import { ModalService } from '@shared/services/modal.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [CommonModule, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'sleip-lidskjalv';
  private auth = inject(Auth);
  private analytics = inject(Analytics);
  private authSub?: Subscription;
  private isCookieModalOpen = false;

  constructor(
    private modalService: ModalService,
    private browserStorageService: BrowserStorageService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (!event.urlAfterRedirects.startsWith('/guides')) {
          this.handleCookieConsent();
        } 
        if(event.urlAfterRedirects.startsWith('/signup')) {
          this.renderer.setAttribute(document.body, 'class', 'mat-typography mat-app-background signup');
        } else {
          this.renderer.setAttribute(document.body, 'class', 'mat-typography mat-app-background');
        }
      });
    this.authTracking();
  }

  ngOnDestroy() {
    this.modalService.close();
    this.authSub?.unsubscribe();
  }

  private authTracking() {
    this.authSub = user(this.auth).subscribe((currentUser) => {
      if (currentUser) {
        setUserId(this.analytics, currentUser.uid);
      } else {
        setUserId(this.analytics, 'anonymous');
      }
    });
  }

  private handleCookieConsent() {
    const hasConsent = this.browserStorageService.getCookieValue(
      CookieKeyEnum.GOOGLE_ANALYTICS_CONSENT_GRANTED,
    );
    this.googleAnalyticsService.initialize(!!hasConsent);

    if (!hasConsent && !this.isCookieModalOpen) {
      /**
       * TODO: Because the cookie Enum was set to "false" as a string.
       * This in turn made it so that we did count the hasConsent as true.
       * Which in turn made it so that we didn't show the cookie consent modal
       * on second time visiting the site.
       *
       * By keeing isCookieModalOpen in app.component.ts we can safely navigate
       * around the app during the session without the cookie modal popping up
       * every time we navigate to a new page. Yet it does trigger second time
       * you navigate to the site.
       */

      this.isCookieModalOpen = true;
      const cookieModalConfig: SleipModalOverlayConfig = {
        hasBackdrop: false,
        backdropClass: 'sleip-modal-overlay-backdrop-remodel',
        data: {
          input: {},
          template: CookieConsentComponent,
          hideCloseButton: true,
          hasBackdrop: false,
        },
        position: ModalPositionEnum.BOTTOM,
        positionOffset: '3rem',
      };
      this.modalService.open(cookieModalConfig);
    }
  }
}
